import ApiPurchasePlans from "api/ApiPurchasePlans"
import BodyText from "components/Typography/BodyText"
import { List } from "hooks/useHandleObjList"
import { PricingUnits } from "pages/purchase-plans/constants/defaultValues"
import { mapSimulatedConsumption } from "pages/purchase-plans/mappers/mapSummary"
import { memo, useEffect, useMemo, useState } from "react"
import { useMutation } from "react-query"
import styled from "styled-components"
import { areArraysEqualUsingSets } from "utils/areArraysEqualUsigSets"
import { uniqueKey } from "utils/uniqueKey"
import DefaultSummary from "./DefaultSummary"

const ContentSummaryItemContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const ResultContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
`

interface ConsumptionSummaryProps {
  isDevMode: boolean
  fromPriceUnit?: string
  selectedProduct?: string
  pricingUnit?: PricingUnits
  pricingVariablesList: List<App.ISimplePricingVariable>
  isUpdate?: boolean
  initialData?: App.IPurchasePlanPayload
}

const ConsumptionSummary = (props: ConsumptionSummaryProps) => {
  const {
    isDevMode,
    fromPriceUnit = "",
    selectedProduct = "",
    pricingUnit,
    pricingVariablesList,
    isUpdate,
    initialData
  } = props

  const [simulatedConsumption, setSimulatedConsumption] = useState<App.ISimulatedConsumption>()

  const summaryContentToConsumption = useMemo(() => {
    if (!simulatedConsumption || isDevMode) return

    return mapSimulatedConsumption(simulatedConsumption)
  }, [simulatedConsumption, fromPriceUnit, isDevMode])

  const calculatePriceBySimulatedConsumption = useMutation({
    mutationFn: async (eventTypes: string[]) => {
      if (eventTypes.some((eventType) => eventType?.length === 0)) return

      const data = await new ApiPurchasePlans().calculatePriceBySimulatedConsumption(eventTypes, selectedProduct)

      return data
    },
    onSuccess: (data) => {
      setSimulatedConsumption(data)
    }
  })

  useEffect(() => {
    if (pricingUnit !== PricingUnits.CONSUMPTION) return

    const eventTypes = pricingVariablesList?.map((pricingVariable) => pricingVariable.eventType) ?? []

    if (isDevMode || eventTypes.length === 0) return

    if (!isUpdate || !initialData?.isSimplifiedPricing) {
      calculatePriceBySimulatedConsumption.mutate(eventTypes)

      return
    }

    if (initialData?.isSimplifiedPricing) {
      const initialEventTypes = initialData.pricingVariables?.map((pricingVariable) => pricingVariable.eventType)

      // validate if initial data is consistent to avoid race condition when calculate summary at first time when is updating
      if (
        !areArraysEqualUsingSets(initialEventTypes, eventTypes) &&
        calculatePriceBySimulatedConsumption.isIdle &&
        isUpdate
      )
        return

      calculatePriceBySimulatedConsumption.mutate(eventTypes)
    }
  }, [pricingVariablesList, initialData, isDevMode, isUpdate, pricingUnit])

  const isValid = !!fromPriceUnit && !!simulatedConsumption && !isDevMode

  return (
    <>
      {!isValid && (
        <ResultContainer>
          <BodyText color="gray-medium" weight="semi-bold">
            Para gerar o resumo, preencha os campos em definições para precificação.
          </BodyText>
        </ResultContainer>
      )}

      {isValid && (
        <DefaultSummary
          totalPrice={summaryContentToConsumption?.totalPrice}
          fromPriceUnit={fromPriceUnit}
          label="Exemplo de consumo"
        >
          <>
            {summaryContentToConsumption?.priceBreakdown?.map((pricingVariable) => (
              <ContentSummaryItemContainer key={uniqueKey()}>
                {pricingVariable.isVisible && (
                  <>
                    <BodyText size="small" weight="light" color="gray-dark">
                      {summaryContentToConsumption?.defaultConsumptionMultiplier} {pricingVariable.label} x{" "}
                      {pricingVariable.formatedEventTypePrice}
                    </BodyText>
                    <BodyText size="small" weight="bold" color="gray-dark">
                      {pricingVariable.formatedPriceBySimulatedConsumption}
                    </BodyText>
                  </>
                )}
              </ContentSummaryItemContainer>
            ))}
          </>
        </DefaultSummary>
      )}
    </>
  )
}

export default memo(ConsumptionSummary)
